import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useUser } from '../hooks/useUser';
import { useStamps } from '../hooks/useStamps';
import { usePendingDocuments } from '../hooks/usePendingDocuments';
import { useHaptic } from '../hooks/useHaptic';
import SubscriptionBanner from '../components/SubscriptionBanner';
import DocumentUploader from '../components/DocumentUploader';
import SideSheet from '../components/SideSheet';
import { Upload, History, Crown, FileText, Loader2, FileCheck, File, Trash2, Stamp } from 'lucide-react';
import './HomePage.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function HomePage() {
  const navigate = useNavigate();
  const { user, limits, loading: userLoading } = useUser();
  const { stamps } = useStamps();
  const { documents: pendingDocuments, loading: docsLoading, deleteDocument, refetch: refetchDocuments } = usePendingDocuments();
  const haptic = useHaptic();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [newDocumentId, setNewDocumentId] = useState(null); // ID нового документа для подсветки

  // Убираем подсветку через 5 секунд
  useEffect(() => {
    if (newDocumentId) {
      const timer = setTimeout(() => {
        setNewDocumentId(null);
      }, 5000); // 5 секунд подсветки
      
      return () => clearTimeout(timer);
    }
  }, [newDocumentId]);

  const handlePendingDocumentClick = (doc) => {
    haptic.itemSelect();
    console.log("📄 Открываем pending документ:", doc);
    navigate('/sign', { 
      state: { 
        pendingDocument: {
          id: doc.id,
          fileName: doc.fileName,
          fileType: doc.fileType,
          isPending: true
        }
      } 
    });
  };

  const handleDeleteDocument = async (e, doc) => {
    e.stopPropagation(); // Предотвращаем всплытие события
    haptic.buttonPressCritical();
    
    const confirmDelete = window.Telegram?.WebApp?.showConfirm 
      ? await new Promise(resolve => {
          window.Telegram.WebApp.showConfirm(
            `Удалить документ "${doc.fileName}"?`,
            resolve
          );
        })
      : window.confirm(`Удалить документ "${doc.fileName}"?`);

    if (confirmDelete) {
      try {
        haptic.delete();
        await deleteDocument(doc.id);
        console.log("✅ Документ удален:", doc.fileName);
        haptic.success();
        
        // Показываем уведомление об успешном удалении
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: "Готово",
            message: "Документ удален",
            buttons: [{ type: "ok" }]
          });
        }
      } catch (error) {
        console.error("❌ Ошибка удаления документа:", error);
        haptic.error();
        
        // Показываем ошибку
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert("Не удалось удалить документ");
        } else {
          alert("Не удалось удалить документ");
        }
      }
    }
  };

  const handleUploadComplete = async (document) => {
    console.log("✅ Документ загружен через webapp:", document);
    setIsUploadModalOpen(false);
    
    // Показываем уведомление об успешной загрузке
    haptic.success();
    
    if (window.Telegram?.WebApp?.showPopup) {
      window.Telegram.WebApp.showPopup({
        title: "Готово",
        message: `Документ загружен и готов к подписанию`,
        buttons: [{ type: "ok" }]
      });
    }
    
    // Обновляем список документов без перезагрузки страницы
    await refetchDocuments();
    
    // Устанавливаем ID нового документа для подсветки
    if (document && document.id) {
      setNewDocumentId(document.id);
    }
  };

  if (userLoading) {
    return (
      <div className="container flex flex-col items-center justify-center" style={{ minHeight: '100vh' }}>
        <Loader2 className="loader-icon" />
        <p className="mt-16">Загрузка...</p>
      </div>
    );
  }

  const hasStamps = stamps && stamps.length > 0;
  const hasPendingDocs = pendingDocuments && pendingDocuments.length > 0;

  console.log("🏠 HomePage состояние:", {
    userLoading,
    docsLoading,
    user,
    limits,
    stamps,
    hasStamps,
    pendingDocuments,
    hasPendingDocs,
    "stamps.length": stamps?.length,
    "pendingDocuments.length": pendingDocuments?.length
  });

  return (
    <div className="home-page container">
      <SubscriptionBanner limits={limits} />

      <div className="main-content">
        {/* Кнопки навигации - показываем всегда */}
        <>
          {/* Главная кнопка управления печатями */}
          <button
            className="action-card-full"
            onClick={() => {
              haptic.navigation();
              navigate('/stamps');
            }}
          >
            <Stamp size={28} className="action-icon" />
            <span className="action-title">Печати и подписи</span>
          </button>

          {/* Кнопка загрузки документа */}
          <button
            className="action-card-full upload-action"
            onClick={() => {
              haptic.buttonPress();
              setIsUploadModalOpen(true);
            }}
          >
            <Upload size={28} className="action-icon" />
            <span className="action-title">Загрузить документ</span>
          </button>

          {/* Остальные кнопки */}
          <div className="actions-grid">
            <button
              className="action-card"
              onClick={() => {
                haptic.navigation();
                navigate('/history');
              }}
            >
              <History size={28} className="action-icon" />
              <span className="action-title">История</span>
            </button>

            <button
              className="action-card subscription-action"
              onClick={() => {
                haptic.buttonPressImportant();
                navigate('/upgrade');
              }}
            >
              <Crown size={28} className="action-icon" />
              <span className="action-title">Подписка</span>
            </button>
          </div>
        </>

        {/* Список pending документов - показываем всегда, если они есть */}
        {hasPendingDocs && (
          <div className="pending-documents-section animate-slide-up animate-delay-100">
            <h2 className="section-title">Документы для подписания</h2>
            <p className="section-subtitle">
              Документы, загруженные через бота и готовые к подписанию
            </p>

            {docsLoading ? (
              <div className="loading-state">
                <Loader2 size={24} className="animate-spin" />
                <span>Загрузка документов...</span>
              </div>
            ) : (
              <div className="pending-documents-grid">
                {pendingDocuments.map((doc, index) => {
                  const thumbnailUrl = `${API_URL}/pending-documents/${doc.id}/thumbnail?initData=${encodeURIComponent(window.Telegram?.WebApp?.initData || '')}`;
                  const isNewDoc = newDocumentId === doc.id;
                  
                  return (
                    <div
                      key={doc.id}
                      className={`pending-document-card stagger-item ${isNewDoc ? 'new-document-highlight' : ''}`}
                      style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                    >
                      {isNewDoc && (
                        <div className="new-document-hint">
                          👇 Нажмите "Подписать" чтобы добавить печати
                        </div>
                      )}
                      <div className="doc-content">
                        <div className="doc-thumbnail-container">
                          <img 
                            src={thumbnailUrl}
                            alt={doc.fileName}
                            className="doc-thumbnail"
                            onError={(e) => {
                              // При ошибке загрузки показываем placeholder с иконкой
                              e.target.style.display = 'none';
                              const placeholder = e.target.parentElement.querySelector('.doc-thumbnail-placeholder');
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                          <div className="doc-thumbnail-placeholder" style={{ display: 'none' }}>
                            <File size={32} />
                          </div>
                        </div>
                        <div className="doc-info">
                          <div className="doc-name-row">
                            <span className="doc-name">{doc.fileName}</span>
                            <button
                              className="delete-button"
                              onClick={(e) => handleDeleteDocument(e, doc)}
                              title="Удалить документ"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <span className="doc-size">
                            {(doc.fileSize / 1024).toFixed(2)} KB
                          </span>
                        </div>
                      </div>
                      <button
                        className="sign-button"
                        onClick={() => handlePendingDocumentClick(doc)}
                      >
                        <FileCheck size={18} />
                        Подписать
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Информационная карточка - показываем только если нет документов */}
        {!hasPendingDocs && !docsLoading && (
          <div className="card info-card">
            <Upload size={48} className="info-icon" />
            <h3>Нет документов для подписания</h3>
            <p>Загрузите документ через веб-приложение или бот</p>
            <button 
              className="button mt-16" 
              onClick={() => {
                haptic.buttonPress();
                setIsUploadModalOpen(true);
              }}
            >
              Загрузить документ
            </button>
          </div>
        )}
      </div>

      {/* Модальное окно для загрузки документа */}
      <SideSheet
        isOpen={isUploadModalOpen}
        onClose={() => {
          haptic.modalClose();
          setIsUploadModalOpen(false);
        }}
        title=""
      >
        <DocumentUploader
          onUploadComplete={handleUploadComplete}
          onCancel={() => setIsUploadModalOpen(false)}
          isOpen={isUploadModalOpen}
        />
      </SideSheet>
    </div>
  );
}

export default HomePage;

